import { Link, useLocation, Outlet } from "react-router-dom";
import { cn } from "@/lib/utils";
import { 
  LayoutDashboard, 
  FileText, 
  User, 
  MessageSquare, 
  CreditCard,
  LogOut,
  GraduationCap,
  BookOpen,
  Home,
  DollarSign,
  Menu,
  X
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { ApplicantOnboarding } from "@/components/onboarding/ApplicantOnboarding";
import { useState } from "react";

export const ApplicantLayout = () => {
  const location = useLocation();
  const { toast } = useToast();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    toast({
      title: "Logged out",
      description: "You have been successfully logged out.",
    });
  };

  const navItems = [
    { icon: LayoutDashboard, label: "Dashboard", path: "/portal" },
    { icon: GraduationCap, label: "Enrollment", path: "/portal/enrollment" },
    { icon: BookOpen, label: "Courses", path: "/portal/courses" },
    { icon: Home, label: "Housing", path: "/portal/housing" },
    { icon: DollarSign, label: "Financial Aid", path: "/portal/financial-aid" },
    { icon: FileText, label: "Documents", path: "/portal/documents" },
    { icon: User, label: "Profile", path: "/portal/profile" },
    { icon: MessageSquare, label: "Messages", path: "/portal/messages" },
    { icon: CreditCard, label: "Payment History", path: "/portal/payments" },
  ];

  const SidebarContent = ({ onItemClick }: { onItemClick?: () => void }) => (
    <>
      <div className="p-6 border-b">
        <h2 className="text-2xl font-bold text-primary">Applicant Portal</h2>
        <p className="text-sm text-muted-foreground mt-1">HTICU</p>
      </div>
      
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path;
          
          return (
            <Link
              key={item.path}
              to={item.path}
              onClick={onItemClick}
              className={cn(
                "flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors min-h-touch",
                isActive 
                  ? "bg-primary text-primary-foreground" 
                  : "text-muted-foreground hover:bg-muted hover:text-foreground"
              )}
            >
              <Icon className="w-5 h-5 flex-shrink-0" />
              <span className="truncate">{item.label}</span>
            </Link>
          );
        })}
      </nav>

      <div className="p-3 border-t">
        <Button
          variant="outline"
          className="w-full justify-start gap-3 min-h-touch"
          onClick={() => {
            handleLogout();
            onItemClick?.();
          }}
        >
          <LogOut className="w-5 h-5" />
          Logout
        </Button>
      </div>
    </>
  );

  return (
    <>
      <ApplicantOnboarding />
      <div className="min-h-screen flex w-full">
        {/* Desktop Sidebar */}
        <aside className="hidden lg:flex w-64 border-r bg-card flex-col">
          <SidebarContent />
        </aside>

        {/* Mobile Header & Sidebar */}
        <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-card border-b px-4 py-3 flex items-center justify-between">
          <h2 className="text-lg font-bold text-primary">Applicant Portal</h2>
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="min-h-touch min-w-touch">
                {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[280px] p-0 flex flex-col">
              <SheetHeader className="sr-only">
                <SheetTitle>Navigation Menu</SheetTitle>
              </SheetHeader>
              <SidebarContent onItemClick={() => setMobileMenuOpen(false)} />
            </SheetContent>
          </Sheet>
        </div>

        {/* Main Content */}
        <main className="flex-1 overflow-auto w-full lg:w-auto pt-[60px] lg:pt-0">
          <Outlet />
        </main>
      </div>
    </>
  );
};
